<template>
  <div class="toast-container" aria-live="polite" aria-atomic="true">
    <transition-group name="toast-slide" tag="div" class="toast-list">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="toast-card"
        :class="`toast-${toast.tipo}`"
        :style="{ '--toast-duracao': `${toast.duracao}ms` }"
        role="status"
      >
        <div class="toast-conteudo">
          <div class="toast-icone-wrapper">
            <i v-if="toast.tipo === 'sucesso'" class="ti ti-circle-check"></i>
            <i v-else-if="toast.tipo === 'erro'" class="ti ti-alert-circle"></i>
            <i v-else-if="toast.tipo === 'aviso'" class="ti ti-alert-triangle"></i>
            <i v-else class="ti ti-info-circle"></i>
          </div>

          <div class="toast-texto">
            <h5 v-if="toast.titulo" class="toast-titulo">{{ toast.titulo }}</h5>
            <p class="toast-mensagem">{{ toast.mensagem }}</p>
          </div>

          <button
            type="button"
            class="toast-btn-fechar"
            @click="remover(toast.id)"
            aria-label="Fechar notificação"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div v-if="toast.duracao > 0" class="toast-barra-container">
          <div class="toast-barra-progresso"></div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
import { onMounted } from "vue";
import { useToast } from "@/composables/useToast";

const { toasts, remover, checkPersisted } = useToast();

onMounted(() => {
  checkPersisted();
});
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 99999;
  pointer-events: none;
  max-width: calc(100vw - 32px);
}

.toast-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;
}

.toast-card {
  pointer-events: auto;
  position: relative;
  width: 380px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.05);
  border: 1.5px solid transparent;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-card:hover {
  transform: translateY(-2px);
  box-shadow:
    0 14px 28px -5px rgba(0, 0, 0, 0.14),
    0 10px 12px -5px rgba(0, 0, 0, 0.06);
}

.toast-card:hover .toast-barra-progresso {
  animation-play-state: paused;
}

.toast-conteudo {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
}

.toast-icone-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.toast-texto {
  flex: 1;
  min-width: 0;
}

.toast-titulo {
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
}

.toast-mensagem {
  margin: 0;
  font-size: 13.5px;
  font-weight: 500;
  color: #4b5563;
  line-height: 1.45;
  word-break: break-word;
}

.toast-btn-fechar {
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-btn-fechar:hover {
  color: #374151;
  background: #f3f4f6;
}

.toast-sucesso {
  border-color: hsl(142, 60%, 82%);
}
.toast-sucesso .toast-icone-wrapper {
  background: hsl(142, 60%, 93%);
  color: hsl(142, 70%, 28%);
}
.toast-sucesso .toast-barra-progresso {
  background: linear-gradient(90deg, hsl(142, 70%, 45%), hsl(142, 75%, 38%));
}

.toast-erro {
  border-color: hsl(0, 70%, 86%);
}
.toast-erro .toast-icone-wrapper {
  background: hsl(0, 80%, 95%);
  color: hsl(0, 75%, 45%);
}
.toast-erro .toast-barra-progresso {
  background: linear-gradient(90deg, hsl(0, 75%, 55%), hsl(0, 80%, 48%));
}

.toast-info {
  border-color: hsl(210, 60%, 85%);
}
.toast-info .toast-icone-wrapper {
  background: hsl(210, 70%, 94%);
  color: hsl(210, 75%, 40%);
}
.toast-info .toast-barra-progresso {
  background: linear-gradient(90deg, hsl(210, 75%, 55%), hsl(210, 80%, 48%));
}

.toast-aviso {
  border-color: hsl(38, 80%, 82%);
}
.toast-aviso .toast-icone-wrapper {
  background: hsl(38, 85%, 93%);
  color: hsl(38, 85%, 35%);
}
.toast-aviso .toast-barra-progresso {
  background: linear-gradient(90deg, hsl(38, 90%, 50%), hsl(38, 95%, 45%));
}

.toast-barra-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: hsl(0, 0%, 93%);
  overflow: hidden;
}

.toast-barra-progresso {
  height: 100%;
  width: 100%;
  transform-origin: left center;
  animation: toastProgressoRegressivo var(--toast-duracao, 5000ms) linear forwards;
}

@keyframes toastProgressoRegressivo {
  0% {
    width: 100%;
  }
  100% {
    width: 0%;
  }
}

.toast-slide-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-slide-enter-from {
  opacity: 0;
  transform: translateX(110%);
}

.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(110%);
}

@media (max-width: 480px) {
  .toast-container {
    top: 16px;
    right: 16px;
    left: 16px;
    max-width: 100%;
  }

  .toast-card {
    width: 100%;
  }
}
</style>
